import React, { useMemo } from 'react';
import { Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { NAVY, PageHeader, StatCard, BandChip, GuidanceBanner, headCell, bodyCell } from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import { labelCase } from '../schoolData';
import {ParticipationBuildupChart} from '../../DashboardCommon/ParticipationBuildupChart.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

// The shared bar + line build-up chart (QA 9 Oct: a single month must still show).
const BuildUpChart = ({ card }) => <ParticipationBuildupChart card={card} title={card?.title || 'Participation build-up'}/>;

const present = (v) => v !== null && v !== undefined && v !== '' && v !== '—';
const numeric = (v) => Number.isFinite(Number(typeof v === 'string' ? v.replace(/%\s*$/, '').replace(/,/g, '') : v));
// The sort/filter meta of a card column: the datahouse marks percents; a column of numbers sorts as
// numbers; the first column is matched by the label it shows ("Grade 7", not "grade 7").
const columnMeta = (col, idx) => {
    const vals = (col.values || []).filter(present);
    const type = col.column_type === 'percent' ? 'percent' : (vals.length > 0 && vals.every(numeric) ? 'number' : 'text');
    return {
        key: col.column_name || `col_${idx}`, label: col.column_name, type,
        accessor: (r) => (idx === 0 ? labelCase(col.values?.[r._i]) : col.values?.[r._i]),
    };
};

// The card's table, one column per table_objects entry. Every column sorts from its header and
// filters once there are enough rows (QA 9 Oct 2026); rows are the card's row indexes.
const TableCard = ({ card }) => {
    const tableObjects = card?.table_objects || [];
    const title = card?.title || 'Participation summary';
    const rowCount = tableObjects[0]?.values?.length || 0;
    const rows = useMemo(() => Array.from({ length: rowCount }, (_, i) => ({ _i: i })), [rowCount]);
    const columns = tableObjects.map(columnMeta);
    const t = useTableControls(rows, columns);

    return (
        <Box sx={{ border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2 }}>{title}</Typography>
            {(t.chips.length > 0 || rowCount >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {tableObjects.map((col, idx) => (
                                <SortFilterHeaderCell key={idx} {...t.headerProps(columns[idx].key)} sx={{ ...headCell, textAlign: idx === 0 ? 'left' : 'center', position: 'sticky', top: 0, zIndex: 1 }}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowCount > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={Math.max(tableObjects.length, 1)} sx={{ ...bodyCell, textAlign: 'center', color: '#6A7282', py: 3 }}>No row matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((r) => {
                            const rowIndex = r._i;
                            return (
                            <TableRow key={rowIndex}>
                                {tableObjects.map((col, colIndex) => {
                                    const val = col.values[rowIndex];
                                    const cellInfo = col.cell_data?.[rowIndex];
                                    const band = cellInfo?.band;
                                    const isLastCol = colIndex === tableObjects.length - 1;

                                    let displayVal = val;
                                    if (col.column_type === 'percent' && typeof val === 'number') {
                                        displayVal = `${val}%`;
                                    }

                                    return (
                                        <TableCell key={colIndex} sx={{ ...bodyCell, textAlign: colIndex === 0 ? 'left' : 'center', fontWeight: colIndex === 0 ? 700 : 'normal', color: colIndex === 0 ? NAVY : '#374151' }}>
                                            {band && isLastCol ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography sx={{ fontSize: '13px' }}>
                                                        {displayVal}
                                                    </Typography>
                                                    <BandChip label={band} />
                                                </Box>
                                            ) : (
                                                colIndex === 0 ? labelCase(displayVal) : displayVal
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const PARTICIPATION_OVERVIEW_SECTION_CODE = 'S_12';
const CARD_SECTION = PARTICIPATION_OVERVIEW_SECTION_CODE;

const ParticipationOverviewTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    const s12Section = useMemo(() => {
        return dashboardData?.cards?.sections?.find(
            (section) => section.section_code === PARTICIPATION_OVERVIEW_SECTION_CODE
        );
    }, [dashboardData]);

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    if (!s12Section) {
        return (
            <NoDataAvailable />
        );
    }

    const cards = s12Section.cards || [];

    // C_1: TitleCard
    const titleCard = cards.find(c => c.card_code === 'C_1');
    const headerTitle = titleCard?.title || 'Participation Overview';
    const headerSubtitle = titleCard?.footer || 'Is the school data representative enough to act on?';
    const eyebrow = ''; // titleCard?.header;

    // C_2 and C_3: KPICards
    const kpiCards = cards.filter(c => c.card_type === 'KPICard');

    // C_5: TrendLineCard
    const trendCard = cards.find(c => c.card_code === 'C_5');

    // C_6: TableCard
    const tableCard = cards.find(c => c.card_code === 'C_6');

    // Resolve Guidance Text (Prefer Coverage Card C_4 if present, otherwise fallback to Participation C_2)
    const coverageKpi = cards.find(c => c.card_code === 'C_4');
    const participationKpi = cards.find(c => c.card_code === 'C_2');

    let guidanceText = '';
    if (coverageKpi) {
        const rate = coverageKpi.kpi_value !== undefined ? `${coverageKpi.kpi_value}%` : '';
        const band = coverageKpi.kpi_band ? ` (${coverageKpi.kpi_band})` : '';
        guidanceText = `Coverage is ${rate}${band}. If participation is uneven across grades or classes, interpret comparisons cautiously.`;
    } else if (participationKpi?.kpi_value !== undefined) {
        const band = participationKpi.kpi_band ? ` (${participationKpi.kpi_band})` : '';
        guidanceText = `Participation is ${participationKpi.kpi_value}%${band}. If participation is uneven across grades or classes, interpret comparisons cautiously.`;
    } else {
        guidanceText = 'If participation is uneven across grades or classes, interpret comparisons cautiously.';
    }

    return (
        <Box>
            <PageHeader
                eyebrow={eyebrow || ''}
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {kpiCards.map((card) => {
                    const valueStr = card.kpi_name === 'PARTICIPATION' ? `${card.kpi_value}%` : card.kpi_value;
                    const subStr = card.kpi_name === 'PARTICIPATION'
                        ? `${card.kpi_data?.participants || 0} of ${card.kpi_data?.total || 0}`
                        : (card.kpi_data?.total ? `of ${card.kpi_data.total}` : '');
                    // "Student Assessed" -> "Students Assessed" — the API's
                    // kpi_name is singular; fixed client-side since this is
                    // just a display label.
                    const displayLabel = (card.kpi_name || '').replace(/^students?\b/i, 'Students');
                    return (
                        <Grid item xs={12} sm={6} md={3} key={card.card_code}>
                            <StatCard
                                topColor={card.kpi_name === 'PARTICIPATION' ? '#1B6B55' : '#3B6FA0'}
                                label={displayLabel}
                                value={valueStr}
                                band={card.kpi_band}
                                sub={subStr}
                            />
                        </Grid>
                    );
                })}
            </Grid>

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={6}>
                    <BuildUpChart card={trendCard} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TableCard card={tableCard} />
                </Grid>
            </Grid>

            <GuidanceBanner text={guidanceText} />
        </Box>
    );
};

export default ParticipationOverviewTab;
